import { CommonModule } from "@angular/common";
import { Component, OnDestroy, ViewChild } from "@angular/core";
import {
  FormBuilder,
  FormGroup,
} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule, MatIconRegistry } from "@angular/material/icon";
import {
  MatPaginator,
  MatPaginatorIntl,
  MatPaginatorModule,
  PageEvent,
} from "@angular/material/paginator";
import {
  MatSidenavContainer,
} from "@angular/material/sidenav";
import { MatSort, MatSortModule } from "@angular/material/sort";
import { MatTableDataSource, MatTableModule } from "@angular/material/table";
import { MatTooltipModule } from "@angular/material/tooltip";
import { CustomMatPaginatorIntl } from "app/mock-api/common/customPaginatorIntl";
import { MatMenuModule } from "@angular/material/menu";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatSnackBar } from "@angular/material/snack-bar";
import { DateHourPipe } from "app/pipes/formated-date.pipe";
import { ReplaySubject, takeUntil, take, Subject, Subscription } from "rxjs";
import { FilterComponent } from "app/common/components/filter/filter.component";
import { SearchService } from "app/layout/common/search-page/search-page.service";
import { TripService } from "../visit-report/visit-report.service";
import { FilterSectionComponent } from "app/common/components/filter-section/filter-section.component";
import { DatesFiltersComponent } from "app/modules/components/filters/dates-filters/dates-filters.component";
import { UserRouteDisplayComponent } from 'app/shared/components/user-route-display/user-route-display.component';
import { HoverMenuDirective } from "@fuse/directives/hover-menu/hover-menu.directive";
import { ColDef, GridConfig, GridReadyEvent, SmartGridComponent, SortChangedEvent } from "app/shared/components/smart-grid";
import { IPaginationResult } from "app/common/interfaces";
import { ActionsCellRendererComponent } from "./cell-renderers/action-cell-renderer.component";
import { RouteUserCellRendererComponent } from "./cell-renderers/route-user-cell-renderer.component";
import { TripStatusCellRendererComponent } from "./cell-renderers/trip-status-cell-renderer.component";
import { TripCellRendererComponent } from "./cell-renderers/trip-cell-renderer.component";
import { DateCellRendererComponent } from "./cell-renderers/date-cell-renderer.component";
import { TimeStampCellRendererComponent } from "./cell-renderers/timeStamp-cell-renderer.component";
import { TripAttributesGps } from "./dto/visit-gps.interface";
import { InactivityCellRendererComponent } from "./cell-renderers/inactivity-cell-renderer.component";
import { StatusIndicatorCellRendererComponent } from "./cell-renderers/indicator-cell-renderer.component";
import { GeoManagerComponent } from "app/common/components/geo-manager/geo-manager.component";
import { MarkerData, MarkerGroup } from "app/common/components/geo-manager/geo-manager.interfaces";
import { FormatOtherDateHourPipe } from "app/pipes/other-date.pipe";
import { MapVisitGpsComponent } from "./map-visit/map-visit.component";
import { MatDialog } from "@angular/material/dialog";
import { extractCustomProps } from "./utils/visit-gps.helper";
import { RoutesFiltersComponent } from "app/modules/components/filters/routes-filters/routes-filters.component";
import { DateHelper } from "app/utils/date.helper";
import { IFilters } from "app/modules/components/filters/interfaces/filterInterface";


@Component({
  selector: "app-visit-gps",
  standalone: true,
  imports: [
    CommonModule,
    MatTableModule,
    MatButtonModule,
    MatSortModule,
    MatPaginatorModule,
    MatIconModule,
    MatTooltipModule,
    MatSidenavContainer,
    MatMenuModule,
    MatDatepickerModule,
    DateHourPipe,
    FilterComponent,
    FilterSectionComponent,
    DatesFiltersComponent,
    UserRouteDisplayComponent,
    HoverMenuDirective,
    SmartGridComponent,
    GeoManagerComponent,
    RoutesFiltersComponent,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl }],
  templateUrl: "./visit-gps.component.html",
})
export class VisitGpsComponent implements OnDestroy {
  dataSource = new MatTableDataSource<TripAttributesGps>([]);

  searchQuery: string = "";
  totalItems: number = 0;
  pageSize: number = 50;
  pageIndex: number = 1;
  sortActive: keyof TripAttributesGps = "dateHourStart";
  sortDirection: "ASC" | "DESC" = "DESC";

  //#region Smart Grid
  // Using `any` because doesn't support TripAttributesGps.
  columnDefs: ColDef<any>[] = [
    //TripAttributesGps
    {
      field: "id",
      headerName: "Viaje",
      cellRenderer: TripCellRendererComponent,
      autoSize: true,
      minWidth: 100,
      sortable: true,
      pinned: "left",
    },
    {
      field: "subRoute",
      headerName: "Ruta - Usuario",
      cellRenderer: RouteUserCellRendererComponent,
      autoSize: true,
      minWidth: 300,
      sortable: true,
      filterOptions: {
        valueExtractor: (item: TripAttributesGps) => item.subRoute?.name,
        customFilterFn(rowData, filterValue) {
          const subRouteName = rowData.subRoute?.name;
          if (!rowData) return !filterValue;

          return subRouteName === filterValue;
        },
      },
      cellRendererParams: {
        handleUserIconClick: (rowData: TripAttributesGps) => {
          this.viewMap(rowData);
        },
      },
    },
    {
      field: "dateHourStart",
      headerName: "Día",
      cellRenderer: DateCellRendererComponent,
      autoSize: true,
      minWidth: 150,
      sortable: true,
      filterOptions: {
        valueExtractor(item) {
          if (!item.dateHourStart) return "";

          const date = new Date(item.dateHourStart);
          return date.toLocaleDateString("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });
        },

        customFilterFn(rowData, filterValue) {
          const rowDate = rowData.dateHourStart;
          if (!rowDate) return !filterValue;

          const rowDateObj = new Date(rowDate);
          const rowDateStr = rowDateObj.toLocaleDateString("es-ES", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });

          return rowDateStr === filterValue;
        },
      },
    },
    {
      headerName: "Última réplica",
      cellRenderer: TimeStampCellRendererComponent,
      autoSize: true,
      minWidth: 150,
      sortable: true,
    },
    {
      headerName: "Inactividad",
      cellRenderer: InactivityCellRendererComponent,
      autoSize: true,
      minWidth: 150,
      sortable: true,
    },
    {
      field: "version",
      headerName: "Versión",
      autoSize: true,
      minWidth: 100,
      sortable: true,
    },
    {
      headerName: "Estado",
      cellRenderer: TripStatusCellRendererComponent,
      autoSize: true,
      minWidth: 150,
      sortable: true,
    },
    {
      headerName: "Indicadores",
      cellRenderer: StatusIndicatorCellRendererComponent,
      autoSize: true,
      minWidth: 100,
      sortable: true,
    },
    {
      headerName: "Acciones",
      cellRenderer: ActionsCellRendererComponent,
      sortable: false,
      autoSize: true,
      minWidth: 120,
      colId: "actions",
      pinned: "right",
      cellRendererParams: {
        mapMenuItem: (rowData: TripAttributesGps) => {
          this.openMap(rowData);
        },
      },
    },
  ];

  gridConfig: GridConfig<any> = {
    rowIdField: "_id" as any,
    emptyState: {
      message: "No encontramos tus resultados!",
      showIcon: true,
    },
    pagination: {
      enabled: true,
      mode: "api",
      pageSize: 50,
      pageSizeOptions: [50, 100, 150, 200],
      showPaginator: true,
    },
    sorting: {
      enabled: true,
      mode: "api",
      multiColumn: false,
      defaultSort: [{ field: "total" as any, direction: "desc" }],
    },
    search: {
      enabled: false,
    },
    columnFilters: {
      enabled: true,
      mode: "local",
    },
    columnVisibility: {
      enabled: false, // Disabled - now in settings panel
    },
    selection: {
      enabled: false,
    },
    autoSizing: {
      enabled: true,
      includeHeaders: true,
      skipHidden: true,
      padding: 40, // Increased for more spacing
    },
    styling: {
      maxHeight: "85vh",
      minHeight: "80vh",
      density: "comfortable",
      fontSize: "12px",
      cellPaddingX: "3px",
      cellPaddingY: "3px",
    },
    settingsPanel: {
      enabled: true,
      position: "right",
      enableColumnVisibility: true,
      defaultDensity: "compact",
    },
  };

  //#endregion

  private destroy$ = new Subject<void>();
  form: FormGroup;
  loadFiltersData = false;

  //#region Map
  @ViewChild("geoManagerTrip", { static: false })
  geoManagerTrip!: GeoManagerComponent;
  filteredMarkerGroups: MarkerGroup[] = [];
  //#endregion

  constructor(
    private tripsService: TripService,
    private readonly searchService: SearchService,
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private fb: FormBuilder
  ) {
    const today = new Date();
    this.form = this.fb.group({
      start: [today],
      end: [today],
      subRouteId: [],
    });
  }

  ngAfterViewInit(): void {
    this.onHandleSearch();
    this.findAllWithCount();
  }

  onClearFilter() {
    this.form.reset({
      start: new Date(),
      end: new Date(),
    });
    this.findAllWithCount();
  }

  findAllWithCount(): void {
    let options = {
      currentPage: this.pageIndex,
      limitPerPage: this.pageSize,
      search: this.searchQuery,
      sort: this.sortActive,
      sortDirection: this.sortDirection,
    };

    let filter: IFilters = {
      startDate: DateHelper.getStartOfDay(this.form.get("start")?.value),
      endDate:
        DateHelper.getEndOfDay(this.form.get("end")?.value) ??
        DateHelper.getEndOfDay(this.form.get("start")?.value) ??
        null,
      subRouteId: this.form.get("subRouteId")?.value,
    };

    this.tripsService
      .findAllWithCountLocations(options, filter)
      .pipe(take(1))
      .subscribe({
        next: (res: IPaginationResult<TripAttributesGps>) => {
          this.dataSource.data = res?.data?.[0]?.data ?? [];
          this.totalItems = res?.data?.[0]?.pageInfo?.[0]?.total ?? 0;
          this.getPointsMap();
        },
        error: (err) => {
          this.snackBar.open(
            err.error.message || "Error al obtener visitas",
            "Cerrar",
            { duration: 5000 }
          );
        },
      });
  }

  changeSort(event: SortChangedEvent<any>) {
    //TripAttributesGps
    if (event.sortStates.length > 0) {
      const sortState = event.sortStates[0];
      this.sortActive = sortState.field as any;
      this.sortDirection = sortState.direction.toUpperCase() as "ASC" | "DESC";
    } else {
      this.sortActive = "dateHourStartTeoric";
      this.sortDirection = "DESC";
    }
    this.findAllWithCount();
  }

  changePage(event: PageEvent): void {
    this.pageIndex = event.pageIndex + 1;
    this.pageSize = event.pageSize;
    this.findAllWithCount();
  }

  onFilterPanelOpened(): void {
    this.loadFiltersData = true;
  }

  onGridReady(event: GridReadyEvent<TripAttributesGps>): void {
    setTimeout(() => {
      event.api.autoSizeAllColumns();
    }, 100);
  }

  public onHandleSearch() {
    this.searchService.searchTerm$
      .pipe(takeUntil(this.destroy$))
      .subscribe((term) => {
        this.searchQuery = term;
        if (this.searchQuery.length >= 2 || this.searchQuery.length === 0) {
          this.pageIndex = 1;
          this.findAllWithCount();
        }
      });
  }

  //#region Map
  private reloadMap(markerGroups: MarkerGroup[]) {
    // Destruir mapa actual
    if (this.geoManagerTrip.map) {
      this.geoManagerTrip.map.remove();
    }

    // Actualizar marcadores y reinicializar
    this.geoManagerTrip.markerGroups = markerGroups;
    this.geoManagerTrip.reloadMap();
  }

  viewMap(trip: TripAttributesGps) {
    if (trip?.locations && trip.locations.length > 0) {
      const firstLocation = trip.locations[0];
      this.geoManagerTrip.showMarkerDetails(
        firstLocation.latitude,
        firstLocation.longitude
      );
    }
  }

  getPointsMap() {
    this.filteredMarkerGroups = [];

    this.dataSource.data.forEach((trip) => {
      const customProps = extractCustomProps(trip);
      if (trip.locations && trip.locations.length > 0) {
        const firstLocation = trip.locations[0];

        let groupColor = "#525252"; // gris
        if (firstLocation.orderCode != null) {
          groupColor = "#2a8ed1";
        } else if (firstLocation.orderCode == null) {
          groupColor = "#008000";
        }

        const marker: MarkerData = {
          title: "Viaje " + trip.id,
          description: trip.subRoute?.name
            ? "Ruta: " + trip.subRoute.name
            : "Sin Ruta",
          latitude: firstLocation.latitude,
          longitude: firstLocation.longitude,
          customProperties: customProps,
        };

        const group = {
          groupName: String(trip.id) ?? "Sin grupo",
          groupColor: groupColor,
          markers: [marker],
        };

        this.filteredMarkerGroups.push(group);
      }
    });

    this.reloadMap(this.filteredMarkerGroups);
  }

  openMap(trip: TripAttributesGps) {
    this.dialog.open(MapVisitGpsComponent, {
      width: "90%",
      data: trip,
      disableClose: true,
    });
  }
  //#endregion

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
